import { ApplicationConfig, provideZoneChangeDetection, ErrorHandler, importProvidersFrom } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { withInMemoryScrolling, withRouterConfig, provideRouter, Routes } from '@angular/router';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { DatePipe, I18nPluralPipe } from '@angular/common';

import { guards } from './routes/guards';
import { routes } from './app.routes';

import { OutdoorAccessApiInterceptor } from '@app/shared-components/interceptors/outdoor-access-api-interceptor';

import { CookieService } from 'ngx-cookie-service';
import { ErrorHandlerService } from '@app/ErrorHandler.service';

import { CarouselModule } from 'ngx-bootstrap/carousel';
import { LandownerAdminSharedModule } from './landowner-admin-shared/landowner-admin-shared.module';
import { ExternalOrgModule } from './external-org/external-org.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { CountdownModule } from 'ngx-countdown';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CommonComponentsModule } from './common-components/common-components.module';
import { TruncateModule } from '@yellowspot/ng-truncate';
import { SharedComponentsModule } from './shared-components/shared-components.module';
import { ServicesModule } from './services/services.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '@env/environment';

import {
    IPublicClientApplication,
    PublicClientApplication,
    InteractionType,
    BrowserCacheLocation,
    LogLevel,
  } from '@azure/msal-browser';
  import {
    MsalInterceptor,
    MSAL_INSTANCE,
    MsalInterceptorConfiguration,
    MsalGuardConfiguration,
    MSAL_GUARD_CONFIG,
    MSAL_INTERCEPTOR_CONFIG,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  } from '@azure/msal-angular';

  export function loggerCallback(logLevel: LogLevel, message: string) {
    //console.log(message);
  }
  
  export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
    auth: {
        clientId: environment.msalConfig.auth.clientId,
        authority: environment.msalConfig.auth.authority,
        knownAuthorities: environment.msalConfig.auth.knownAuthorities,
        redirectUri: "/", // This is your redirect URI,
        navigateToLoginRequestUrl: true
    },
    cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
        allowPlatformBroker: false, // Disables WAM Broker
        loggerOptions: {
          loggerCallback,
          logLevel: LogLevel.Verbose,
          piiLoggingEnabled: true
        }
      }
    });
  }

  export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const apiUrl = environment.apiConfig.uri;
    const protectedResourceMap = new Map([
        [apiUrl + "public/*",  null],
        [apiUrl + "*", environment.apiConfig.scopes]
      ]);
  
    return {
      interactionType: InteractionType.Redirect,
      protectedResourceMap
    };
  }
    
  export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return { 
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: environment.apiConfig.scopes
      },
      loginFailedRoute: '/login-failed'
    };
  }

  export const appConfig: ApplicationConfig = {
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true }), 
      provideRouter(
          routes, 
          withInMemoryScrolling({ 
              scrollPositionRestoration: 'enabled', 
              anchorScrolling: 'enabled' 
          }), 
          withRouterConfig({ 
              onSameUrlNavigation: 'reload' 
          })
      ),
      importProvidersFrom(
          BrowserModule, 
          BrowserAnimationsModule, 
          ToastrModule.forRoot(), // Add ToastrModule here with proper configuration
          ServicesModule, 
          SharedComponentsModule, 
          TruncateModule, 
          CommonComponentsModule, 
          BsDropdownModule.forRoot(), 
          ModalModule.forRoot(), 
          PopoverModule.forRoot(), 
          NgxPageScrollCoreModule, 
          NgxPageScrollModule, 
          CountdownModule, 
          FormsModule, 
          NgSelectModule, 
          ExternalOrgModule, 
          LandownerAdminSharedModule, 
          CarouselModule),
      guards,
      DatePipe,
      I18nPluralPipe,
      { 
          provide: HTTP_INTERCEPTORS, 
          useClass: OutdoorAccessApiInterceptor, 
          multi: true 
      },

      {
          provide: ErrorHandler, 
          useClass: ErrorHandlerService
      },

      {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true
      },
      {
          provide: MSAL_INSTANCE,
          useFactory: MSALInstanceFactory
      },
      {
          provide: MSAL_GUARD_CONFIG,
          useFactory: MSALGuardConfigFactory
      },
      {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: MSALInterceptorConfigFactory
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,

      
      CookieService,
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimations(),
          
    ]

  };
